import { formatStringUrl, setEnvByServer } from "application";
import { accessTokenCookieKey } from "application/utils/http/refresh-token/keys";
import {
  appMonitoringClient,
  backofficeApi,
  catalogApi,
  cookie,
  envsApi,
  shoppingApi,
} from "implementations";
import { GetServerSideProps } from "next";
import { ApiDefaultErrorDto, CartDto, Product as ProductType } from "typing";
import Product from "../../../../src/screens/Product";
import { isBlackFridayServerSideProps } from "../../../../src/utils/server-side";

const handleFetchCart = async (
  accessToken?: string,
  anonymousToken?: string
) => {
  if (accessToken) {
    const { data } = await shoppingApi.fetchCartAuthenticated(accessToken);
    return data as CartDto;
  }

  if (anonymousToken) {
    const { data } = await shoppingApi.fetchCartAnonymous(anonymousToken);
    return data as CartDto;
  }

  return "";
};

export const getServerSideProps: GetServerSideProps<{
  dataProduct?: ProductType | ApiDefaultErrorDto;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}> = async ({ query, req, res }) => {
  try {
    await setEnvByServer("@FC:Ecom:APIS_BASE_URL", envsApi, catalogApi, req);

    const { data: siteUrl } = envsApi.getRawEnvImmediately("SITE_URL");
    const accessToken = req.cookies[`${accessTokenCookieKey}`];
    const TOKEN_COOKIE_ANONYMOUS = req.cookies["@FC:Ecom:TokenAnonymous"];
    const rawRegionIdFromQueryParam = query?.region_id;
    const regionIdFromQueryParam = Number(rawRegionIdFromQueryParam?.[0]);
    const cart = (await handleFetchCart(
      accessToken,
      TOKEN_COOKIE_ANONYMOUS
    )) as CartDto;

    const currentBranchId = Number(
      cookie.getCookie({
        name: "@FC:Ecom:Dropdown:BranchLocation",
        options: { req, res },
      }) ?? "2"
    );

    const productId = Number(query?.idProduct);

    const finalRegionId =
      !Number.isNaN(regionIdFromQueryParam) && regionIdFromQueryParam > 0
        ? regionIdFromQueryParam
        : currentBranchId;

    const response = await catalogApi.fetchProduct(productId, finalRegionId);

    if (response?.error) {
      // eslint-disable-next-line no-console
      console.log(response.error);
      return { props: {}, notFound: true };
    }

    const data = { ...response.data } as ProductType;

    if (!data.description) {
      // eslint-disable-next-line no-console
      console.log(
        `A api de detalhe do produto não retornou nenhum dado.\nId do produto: ${productId}\nRegião do cliente: ${finalRegionId}`
      );
      return { props: {}, notFound: true };
    }

    const blackFridayTheme = await isBlackFridayServerSideProps(backofficeApi);

    // const { maxAge, staleWhileRevalidate } = getPageCacheControlInfo(
    //   process.env.PRODUCT_PAGE_STALE_WHILE_REVALIDATE
    // );

    // context.res?.setHeader(
    //   "Cache-Control",
    //   `public, s-maxage=${maxAge}, stale-while-revalidate=${staleWhileRevalidate}`
    // );

    let hasChangesInClientBranchFromRegionIdParam = false;

    if (regionIdFromQueryParam && regionIdFromQueryParam !== currentBranchId) {
      if (cart?.cartItems?.length) {
        return {
          props: {},
          redirect: {
            destination: "/",
          },
        };
      }

      hasChangesInClientBranchFromRegionIdParam = true;
    }

    const formattedProductDescription = formatStringUrl(
      data?.description || ""
    );

    if (
      formattedProductDescription &&
      formattedProductDescription !== query?.nameProduct
    ) {
      const formattedUrl = encodeURI(
        `/produto/${productId}/${formattedProductDescription}${
          rawRegionIdFromQueryParam
            ? `?region_id=${rawRegionIdFromQueryParam}`
            : ""
        }`
      );

      return {
        redirect: {
          permanent: false,
          destination: formattedUrl,
        },
      };
    }

    const { data: schema, error: schemaError } = await catalogApi.fetchSchema({
      ...data,
    });

    if (schemaError || !schema) {
      appMonitoringClient.captureMessage("Erro ao buscar schema do produto", {
        tags: {
          productId,
          branchId: finalRegionId,
        },
      });

      appMonitoringClient.captureException(schemaError);
    }

    return {
      props: {
        dataProduct: data,
        blackFridayTheme,
        hasChangesInClientBranchFromRegionIdParam,
        siteUrl,
        cartId: cart?.id || null,
        productId,
        branchId: finalRegionId,
        schema: schema || null,
      },
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    appMonitoringClient.captureException(error);
    return { props: {}, notFound: true };
  }
};

export default Product;
